import { isEqual, omitBy } from 'lodash';
import * as qs from 'query-string';

import * as OvationsApi from 'core/ovations-api';

import apiFetch from './apiFetch';
import { API_URL } from './constants';

export default class BatchConfiguration {
    static defaultRequest: Partial<OvationsApi.Types.BatchConfigurationRequest> = {
        page: 1,
    };

    static serializeRequest(
        batchConfigurationRequest: Partial<OvationsApi.Types.BatchConfigurationRequest>,
        includeDefaults?: boolean,
    ): string {
        let request = batchConfigurationRequest;
        if (!includeDefaults) {
            request = omitBy(batchConfigurationRequest, (value, key) => {
                return (
                    key in BatchConfiguration.defaultRequest && isEqual(value, BatchConfiguration.defaultRequest[key])
                );
            });
        }
        return qs.stringify(request, { arrayFormat: 'index' });
    }

    static async fetchAll(
        clientId: string,
        programId: string,
        isConfig?: boolean,
    ): Promise<OvationsApi.Types.SearchResultsResponse<OvationsApi.Types.BatchConfiguration>> {
        if (!isConfig) {
            return this.fetchAllJobs(clientId, programId);
        }
        return this.fetchAllConfigs(clientId, programId);
    }

    static async fetchAllJobs(
        clientId: string,
        programId: string,
    ): Promise<OvationsApi.Types.SearchResultsResponse<OvationsApi.Types.BatchConfiguration>> {
        const url = `${API_URL}/api/v1/client/${clientId}/program/${programId}/batchConfiguration`;
        const response = await apiFetch(url);
        const json = await response.json();
        return json;
    }

    static async fetchAllConfigs(
        clientId: string,
        programId: string,
    ): Promise<OvationsApi.Types.SearchResultsResponse<OvationsApi.Types.BatchConfiguration>> {
        const url = `${API_URL}/api/v1/client/${clientId}/program/${programId}/batchConfiguration/configurations`;
        const response = await apiFetch(url);
        const json = await response.json();
        return json;
    }

    static async search(
        clientId: string,
        programId: string,
        request: Partial<OvationsApi.Types.BatchConfigurationRequest>,
        isConfig?: boolean,
    ): Promise<OvationsApi.Types.SearchResultsResponse<OvationsApi.Types.BatchConfiguration>> {
        if (!isConfig) {
            return this.searchJobs(clientId, programId, request);
        }
        return this.searchConfigs(clientId, programId, request);
    }

    static async searchJobs(
        clientId: string,
        programId: string,
        request: Partial<OvationsApi.Types.BatchConfigurationRequest>,
    ): Promise<OvationsApi.Types.SearchResultsResponse<OvationsApi.Types.BatchConfiguration>> {
        const query = BatchConfiguration.serializeRequest(request, true);
        const url = `${API_URL}/api/v1/client/${clientId}/program/${programId}/batchConfiguration${query}`;
        const response = await apiFetch(url);
        const json = await response.json();
        return json;
    }

    static async searchConfigs(
        clientId: string,
        programId: string,
        request: Partial<OvationsApi.Types.BatchConfigurationRequest>,
    ): Promise<OvationsApi.Types.SearchResultsResponse<OvationsApi.Types.BatchConfiguration>> {
        const query = BatchConfiguration.serializeRequest(request, true);
        const url = `${API_URL}/api/v1/client/${clientId}/program/${programId}/batchConfiguration/configurations?${query}`;
        const response = await apiFetch(url);
        const json = await response.json();
        return json;
    }

    static async create(clientId: string, batchConfiguration: OvationsApi.Types.BatchConfiguration): Promise<number> {
        const url = `
      ${API_URL}/api/v1/client/${clientId}/program/${batchConfiguration.programId}/BatchConfiguration
    `;
        const response = await apiFetch(url, {
            headers: [['Content-Type', 'application/json']],
            method: 'POST',
            body: JSON.stringify(batchConfiguration),
        });
        const newId = await response.json();
        return newId;
    }

    static isReloadable(batchConfiguration: OvationsApi.Types.BatchConfiguration): boolean {
        return (
            batchConfiguration.batchType === OvationsApi.Enums.BatchType.ReloadableCAN ||
            batchConfiguration.batchType === OvationsApi.Enums.BatchType.ReloadableCANVisa ||
            batchConfiguration.batchType === OvationsApi.Enums.BatchType.ReloadableUSA ||
            batchConfiguration.batchType === OvationsApi.Enums.BatchType.ReloadableUSAGalileo
        );
    }

    static isCheck(batchConfiguration: OvationsApi.Types.BatchConfiguration): boolean {
        return (
            batchConfiguration.batchType === OvationsApi.Enums.BatchType.CheckUSA ||
            batchConfiguration.batchType === OvationsApi.Enums.BatchType.CheckCAN ||
            batchConfiguration.batchType === OvationsApi.Enums.BatchType.CheckReplacements
        );
    }

    static async archive(
        clientId: string,
        batchConfiguration: OvationsApi.Types.BatchConfiguration,
    ): Promise<Response> {
        const url = `${API_URL}/api/v1/client/${clientId}/program/${batchConfiguration.programId}/BatchConfiguration/${batchConfiguration.id}`;
        return await apiFetch(url, {
            headers: [['Content-Type', 'application/json']],
            method: 'DELETE',
        });
    }
}
